import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FileText, Send, CheckCircle, Copy, ExternalLink, Trash2, Download } from 'lucide-react';

/**
 * ContractSigning — Digital contract management for the wedding pipeline.
 *
 * Two views:
 *  1. STAFF VIEW (inside Pipeline detail) — upload/generate contract, share a signing link,
 *     view signed status, download signed copy.
 *  2. PUBLIC SIGNING PAGE — will be a separate route, but the signature pad is embedded here
 *     for now as a modal the staff can preview / test.
 *
 * Contract data is stored in event fields:
 *   - contractUrl: link to the contract PDF or stored file
 *   - contractSignedUrl: the signed version / signature data URL
 *   - contractSignedAt: ISO timestamp
 */

/* ── Inline Signature Pad ── */
function SignaturePad({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Set canvas size to match display size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
    // Draw guide line
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#d1d5db';
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearPad = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    // Redraw guide line
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#d1d5db';
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
    setHasDrawn(false);
  };

  const handleSave = () => {
    if (!hasDrawn) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Please sign below to confirm the contract.</p>
      <canvas
        ref={canvasRef}
        className="w-full border-2 border-gray-300 rounded-lg cursor-crosshair bg-white"
        style={{ height: 160, touchAction: 'none' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={!hasDrawn}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition ${
            hasDrawn ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'
          }`}
          style={{ backgroundColor: '#1A5C3A' }}
        >
          <CheckCircle size={16} />
          Confirm Signature
        </button>
        <button
          onClick={clearPad}
          className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
        >
          Clear
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main Contract Management Component ── */
export default function ContractSigning({ event, onUpdate, onSendEmail }) {
  const [showSignPad, setShowSignPad] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailAddress, setEmailAddress] = useState(event?.clientEmail || '');

  const isSigned = !!event?.contractSignedAt;
  const hasContract = !!event?.contractUrl;

  // Generate a shareable signing link (in production this would be a proper URL)
  const signingLink = hasContract
    ? `${window.location.origin}/sign/${event.id}`
    : null;

  const handleCopyLink = useCallback(() => {
    if (signingLink) {
      navigator.clipboard.writeText(signingLink).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [signingLink]);

  const handleSendContractEmail = useCallback(() => {
    if (!emailAddress || !signingLink) return;
    // If parent provided an email handler, use it
    if (onSendEmail) {
      onSendEmail({
        to: emailAddress,
        subject: `Wedding Contract — ${event?.name || 'Your Wedding'}`,
        body: `Dear ${event?.client || 'Client'},\n\nPlease find your wedding contract ready for review and signing at the link below:\n\n${signingLink}\n\nPlease review the contract and sign digitally at your convenience.\n\nKind regards,\nErrigal Country House Hotel`,
        signingLink,
      });
    }
    setEmailSent(true);
    setShowEmailForm(false);
    setTimeout(() => setEmailSent(false), 3000);
  }, [emailAddress, signingLink, onSendEmail, event]);

  const handleContractUpload = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // In production: upload to Supabase Storage and store the URL.
      // For now, store a local object URL as proof of concept.
      const url = URL.createObjectURL(file);
      onUpdate({
        contractUrl: url,
        contractSignedUrl: '',
        contractSignedAt: null,
      });
    },
    [onUpdate]
  );

  const handleSignatureSaved = useCallback(
    (dataUrl) => {
      onUpdate({
        contractSignedUrl: dataUrl,
        contractSignedAt: new Date().toISOString(),
      });
      setShowSignPad(false);
    },
    [onUpdate]
  );

  const handleRemoveContract = useCallback(() => {
    if (window.confirm('Remove the uploaded contract? This will also clear any signature.')) {
      onUpdate({
        contractUrl: '',
        contractSignedUrl: '',
        contractSignedAt: null,
      });
    }
  }, [onUpdate]);

  return (
    <div className="space-y-4">
      <h4 className="font-bold text-sm text-gray-900 flex items-center gap-2">
        <FileText size={16} className="text-emerald-700" />
        Contract
      </h4>

      {/* Upload / Status */}
      {!hasContract ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <FileText size={28} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 mb-3">Upload the wedding contract (PDF)</p>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer hover:opacity-90 transition" style={{ backgroundColor: '#1A5C3A' }}>
            <FileText size={16} />
            Choose File
            <input type="file" accept=".pdf,.doc,.docx" onChange={handleContractUpload} className="hidden" />
          </label>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Contract uploaded row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-emerald-600" />
              <span className="text-sm font-medium text-gray-900">Contract uploaded</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={event.contractUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
              >
                <ExternalLink size={12} /> View
              </a>
              <button
                onClick={handleRemoveContract}
                className="text-xs text-red-600 hover:underline flex items-center gap-1"
              >
                <Trash2 size={12} /> Remove
              </button>
            </div>
          </div>

          {/* Signing status */}
          {isSigned ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3">
              <CheckCircle size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-800">Contract Signed</p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  Signed on {new Date(event.contractSignedAt).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                {event.contractSignedUrl && (
                  <div className="mt-2">
                    <img
                      src={event.contractSignedUrl}
                      alt="Signature"
                      className="h-12 border border-gray-200 rounded bg-white px-2"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Share signing link */}
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={signingLink || ''}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs bg-gray-50 text-gray-700"
                />
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium border border-gray-300 hover:bg-gray-50 transition"
                >
                  <Copy size={14} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Share this link or email it directly to the couple.
              </p>

              {/* Email contract to customer */}
              {!showEmailForm ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEmailAddress(event?.clientEmail || ''); setShowEmailForm(true); }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition hover:opacity-90"
                    style={{ backgroundColor: '#1A5C3A' }}
                  >
                    <Send size={16} />
                    Email to Customer
                  </button>
                  <button
                    onClick={() => setShowSignPad(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition"
                  >
                    Sign Now
                  </button>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                  <label className="block text-xs font-medium text-gray-700">Send contract to:</label>
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    placeholder="client@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <p className="text-xs text-gray-500">
                    This will email the signing link to the couple with instructions to review and sign.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSendContractEmail}
                      disabled={!emailAddress}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition ${emailAddress ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'}`}
                      style={{ backgroundColor: '#1A5C3A' }}
                    >
                      <Send size={14} />
                      Send Email
                    </button>
                    <button
                      onClick={() => setShowEmailForm(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {emailSent && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium">
                  <CheckCircle size={14} />
                  Contract email sent!
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Signature Pad Modal */}
      {showSignPad && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Sign Contract</h3>
            <p className="text-sm text-gray-600 mb-4">{event?.name}</p>
            <SignaturePad
              onSave={handleSignatureSaved}
              onCancel={() => setShowSignPad(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
